/**
 * Create Speurtocht Screen
 * Form to create a new speurtocht
 */

import React, { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Switch,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useSpeurttochtAdmin, DEFAULT_POINTS_CONFIG } from "@/modules/speurtocht";

/** Available icons for speurtocht */
const AVAILABLE_ICONS = [
  "map",
  "compass",
  "navigate",
  "footsteps",
  "trail-sign",
  "flag",
  "trophy",
  "star",
  "diamond",
  "flash",
  "rocket",
  "planet",
];

/** Available colors for speurtocht */
const AVAILABLE_COLORS = [
  "#10B981", // Green
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#14B8A6", // Teal
  "#6366F1", // Indigo
];

export default function CreateSpeurttochtScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, activeProfile } = useAuth();
  const { createSpeurtocht, isSubmitting, error } = useSpeurttochtAdmin();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("map");
  const [color, setColor] = useState("#10B981");
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [maxDuration, setMaxDuration] = useState("60");
  const [pointsPerStep, setPointsPerStep] = useState(
    String(DEFAULT_POINTS_CONFIG.points_per_step)
  );
  const [completionBonus, setCompletionBonus] = useState(
    String(DEFAULT_POINTS_CONFIG.completion_bonus)
  );
  const [firstTeamBonus, setFirstTeamBonus] = useState(
    String(DEFAULT_POINTS_CONFIG.first_team_bonus)
  );
  const [requirePhotoVerification, setRequirePhotoVerification] = useState(false);
  const [requireLocationVerification, setRequireLocationVerification] = useState(false);
  const [showMapToUsers, setShowMapToUsers] = useState(true);
  const [showAllSteps, setShowAllSteps] = useState(false);

  /**
   * Handle create
   */
  const handleCreate = async () => {
    if (!name.trim()) {
      Alert.alert("Fout", "Voer een naam in voor de speurtocht");
      return;
    }

    if (!activeWeekend?.id) {
      Alert.alert("Fout", "Er is geen actief weekend");
      return;
    }

    const result = await createSpeurtocht({
      weekend_id: activeWeekend.id,
      name: name.trim(),
      description: description.trim() || undefined,
      icon,
      color,
      has_time_limit: hasTimeLimit,
      max_duration_minutes: hasTimeLimit ? parseInt(maxDuration) || 60 : undefined,
      points_per_step: parseInt(pointsPerStep) || DEFAULT_POINTS_CONFIG.points_per_step,
      completion_bonus: parseInt(completionBonus) || DEFAULT_POINTS_CONFIG.completion_bonus,
      first_team_bonus: parseInt(firstTeamBonus) || DEFAULT_POINTS_CONFIG.first_team_bonus,
      require_photo_verification: requirePhotoVerification,
      require_location_verification: requireLocationVerification,
      show_map_to_users: showMapToUsers,
      show_all_steps: showAllSteps,
    });

    if (result) {
      router.replace(`/(admin)/games/speurtocht/${result.id}/steps`);
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Nieuwe Speurtocht
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleCreate}
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 rounded-xl flex-row items-center"
            style={{
              backgroundColor: name.trim() ? "#10B981" : colors.surfaceSecondary,
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons
                  name="checkmark"
                  size={18}
                  color={name.trim() ? "#FFFFFF" : colors.textTertiary}
                />
                <Text
                  className="ml-1 font-bold"
                  style={{ color: name.trim() ? "#FFFFFF" : colors.textTertiary }}
                >
                  Aanmaken
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
          {/* Error Message */}
          {error && (
            <View
              className="p-3 rounded-xl mb-4"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Text style={{ color: colors.error }}>{error}</Text>
            </View>
          )}

          {/* Preview Card */}
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: `${color}15`, borderWidth: 2, borderColor: color }}
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center"
                style={{ backgroundColor: `${color}25` }}
              >
                <Ionicons name={icon as any} size={24} color={color} />
              </View>
              <View className="ml-3 flex-1">
                <Text className="font-bold text-lg" style={{ color: colors.text }}>
                  {name || "Speurtocht naam"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  {description || "Beschrijving"}
                </Text>
              </View>
            </View>
          </View>

          {/* Basic Info Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Basis Informatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            {/* Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Naam *
              </Text>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder="Bijv. De Grote Speurtocht"
                placeholderTextColor={colors.textTertiary}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
            </View>

            {/* Description */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Beschrijving
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                placeholder="Korte beschrijving van de speurtocht"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={3}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />
            </View>

            {/* Icon Selection */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Icoon
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_ICONS.map((iconName) => (
                  <TouchableOpacity
                    key={iconName}
                    onPress={() => setIcon(iconName)}
                    className="w-10 h-10 rounded-lg items-center justify-center mr-2 mb-2"
                    style={{
                      backgroundColor:
                        icon === iconName ? `${color}25` : colors.surfaceSecondary,
                      borderWidth: icon === iconName ? 2 : 0,
                      borderColor: color,
                    }}
                  >
                    <Ionicons
                      name={iconName as any}
                      size={20}
                      color={icon === iconName ? color : colors.textSecondary}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Color Selection */}
            <View>
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Kleur
              </Text>
              <View className="flex-row flex-wrap">
                {AVAILABLE_COLORS.map((colorOption) => (
                  <TouchableOpacity
                    key={colorOption}
                    onPress={() => setColor(colorOption)}
                    className="w-10 h-10 rounded-lg items-center justify-center mr-2 mb-2"
                    style={{
                      backgroundColor: colorOption,
                      borderWidth: color === colorOption ? 3 : 0,
                      borderColor: colors.text,
                    }}
                  >
                    {color === colorOption && (
                      <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* Time Limit Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Tijdslimiet
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Tijdslimiet inschakelen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Teams hebben beperkte tijd om te voltooien
                </Text>
              </View>
              <Switch
                value={hasTimeLimit}
                onValueChange={setHasTimeLimit}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={hasTimeLimit ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            {hasTimeLimit && (
              <View>
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Maximale duur (minuten)
                </Text>
                <TextInput
                  value={maxDuration}
                  onChangeText={setMaxDuration}
                  keyboardType="number-pad"
                  placeholder="60"
                  placeholderTextColor={colors.textTertiary}
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
            )}
          </View>

          {/* Points Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Punten
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row mb-4">
              <View className="flex-1 mr-2">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Per stap
                </Text>
                <TextInput
                  value={pointsPerStep}
                  onChangeText={setPointsPerStep}
                  keyboardType="number-pad"
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
              <View className="flex-1 ml-2">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Voltooiings bonus
                </Text>
                <TextInput
                  value={completionBonus}
                  onChangeText={setCompletionBonus}
                  keyboardType="number-pad"
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
              </View>
            </View>

            <View>
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Eerste team bonus
              </Text>
              <TextInput
                value={firstTeamBonus}
                onChangeText={setFirstTeamBonus}
                keyboardType="number-pad"
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
              <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                Extra punten voor het eerste team dat voltooit
              </Text>
            </View>
          </View>

          {/* Verification Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Verificatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Foto verificatie vereist
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Admin moet foto-inzendingen goedkeuren
                </Text>
              </View>
              <Switch
                value={requirePhotoVerification}
                onValueChange={setRequirePhotoVerification}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={requirePhotoVerification ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            <View className="flex-row items-center justify-between">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Locatie verificatie vereist
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Admin moet locatie check-ins goedkeuren
                </Text>
              </View>
              <Switch
                value={requireLocationVerification}
                onValueChange={setRequireLocationVerification}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={requireLocationVerification ? "#10B981" : colors.surfaceSecondary}
              />
            </View>
          </View>

          {/* Display Section */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Weergave
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-4">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Kaart tonen aan gebruikers
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Toon interactieve kaart bij locatie-stappen
                </Text>
              </View>
              <Switch
                value={showMapToUsers}
                onValueChange={setShowMapToUsers}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={showMapToUsers ? "#10B981" : colors.surfaceSecondary}
              />
            </View>

            <View className="flex-row items-center justify-between">
              <View className="flex-1 pr-4">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Alle stappen tonen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Toon alle stappen of alleen de huidige
                </Text>
              </View>
              <Switch
                value={showAllSteps}
                onValueChange={setShowAllSteps}
                trackColor={{ false: colors.border, true: "#10B98180" }}
                thumbColor={showAllSteps ? "#10B981" : colors.surfaceSecondary}
              />
            </View>
          </View>

          <View className="h-32" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

